import { computed, ref } from 'vue';
import { useQuery, useMutation } from '@vue/apollo-composable';
import {
  STUDENT_ANNOUNCEMENTS,
  PENDING_ANNOUNCEMENT_ACKNOWLEDGEMENTS,
  MARK_SCHOOL_ANNOUNCEMENT_READ,
  ACKNOWLEDGE_SCHOOL_ANNOUNCEMENT,
} from '@/graphql/schoolAnnouncements';
import type { SchoolAnnouncement, SchoolAnnouncementSeverity } from '@/types/composition-functions';

/**
 * Per-severity presentation, expressed as PrimeVue severities rather than
 * fixed colours.
 *
 * A hardcoded palette would ignore the school's own primary colour, which
 * useBranding swaps into the theme at runtime — a re-branded school would get
 * everything else in its colours and these chips in someone else's. Handing
 * PrimeVue a severity lets the active theme decide the actual hue.
 */
const SEVERITY_STYLES: Record<SchoolAnnouncementSeverity, {
  label: string; icon: string; severity: 'danger' | 'warn' | 'info';
}> = {
  URGENT: { label: 'Urgent', icon: 'pi-exclamation-triangle', severity: 'danger' },
  IMPORTANT: { label: 'Important', icon: 'pi-info-circle', severity: 'warn' },
  INFO: { label: 'Notice', icon: 'pi-megaphone', severity: 'info' },
};

export function severityStyle(severity: SchoolAnnouncementSeverity | undefined) {
  return SEVERITY_STYLES[severity ?? 'INFO'] ?? SEVERITY_STYLES.INFO;
}

/**
 * The student's notice board.
 *
 * `unreadCount` drives the sidebar badge, so it counts notices with no
 * read_at — an acknowledged notice is by definition read and drops out.
 */
export function useStudentAnnouncements() {
  const { result, loading, refetch } = useQuery<{ studentAnnouncements: SchoolAnnouncement[] }>(
    STUDENT_ANNOUNCEMENTS,
    null,
    { fetchPolicy: 'cache-and-network' },
  );

  const notices = computed<SchoolAnnouncement[]>(() => result.value?.studentAnnouncements ?? []);
  const unreadCount = computed(() => notices.value.filter((n) => !n.read_at).length);

  const { mutate: markReadMutation } = useMutation(MARK_SCHOOL_ANNOUNCEMENT_READ);

  /**
   * Opening a notice records the read. Failure is swallowed on purpose: a
   * receipt that did not save must not stop the student reading the notice,
   * and the next open will try again.
   */
  const markRead = async(notice: SchoolAnnouncement): Promise<void> => {
    if (notice.read_at) return;
    try {
      await markReadMutation({ id: notice.id });
      await refetch();
    } catch {
      // non-fatal — see above
    }
  };

  return {
    notices, unreadCount, loading, refetch, markRead,
  };
}

/**
 * The must-read gate.
 *
 * Returns the queue of notices the student still owes an acknowledgement for.
 * The caller renders them one at a time; `acknowledge` advances the queue and
 * the gate closes when it empties.
 */
export function usePendingAnnouncements() {
  const { result, loading, refetch } = useQuery<{
    pendingAnnouncementAcknowledgements: SchoolAnnouncement[];
  }>(PENDING_ANNOUNCEMENT_ACKNOWLEDGEMENTS, null, { fetchPolicy: 'cache-and-network' });

  const acknowledging = ref(false);
  const pending = computed<SchoolAnnouncement[]>(
    () => result.value?.pendingAnnouncementAcknowledgements ?? [],
  );

  const { mutate: acknowledgeMutation } = useMutation(ACKNOWLEDGE_SCHOOL_ANNOUNCEMENT);

  /**
   * Unlike markRead, a failure here MUST surface: silently treating a failed
   * acknowledgement as done would let the student past a gate the server still
   * considers closed, and the block would reappear on the next page load with
   * no explanation.
   */
  const acknowledge = async(notice: SchoolAnnouncement): Promise<boolean> => {
    acknowledging.value = true;
    try {
      await acknowledgeMutation({ id: notice.id });
      await refetch();
      return true;
    } catch {
      return false;
    } finally {
      acknowledging.value = false;
    }
  };

  return {
    pending, loading, acknowledging, acknowledge, refetch,
  };
}
